import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { ResolvedEndUserThemeSchema } from '../customer/end-user-theme.schema'

const c = initContract()

// Customer Schema
const IntegrationSchema = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  features: z.any().optional().nullable(),
})

const CustomerSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string().optional().nullable(),
  integrations: z.array(IntegrationSchema).optional().nullable(),
  endUserTheme: ResolvedEndUserThemeSchema.optional().nullable(),
})

// Define the campaign schema - using passthrough() to allow additional fields
// This prevents Zod validation errors when the API returns more fields than explicitly defined
const CampaignSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    typeOfCampaign: z.string().nullable().optional(),
    companyName: z.string().nullable().optional(),
    startDate: z.string(),
    endDate: z.string(),
    details: z.any().nullable().optional(),
    referralCampaign: z.boolean().nullable().optional(),
    isDraft: z.boolean(),
    postcardTemplate: z.any().nullable().optional(),
    emailTemplate: z.any().nullable().optional(),
    updatedAt: z.string(),
    completedStep: z.number(),
    printFilesLink: z.string().nullable().optional(),
    printPassword: z.string().nullable().optional(),
    campaignCategory: z.string().nullable().optional(),
    campaignStatus: z.string().nullable().optional(),
    // A campaign carries whatever its endpoint selected of the customer:
    // getCampaign sends the theme, getLatestPurchasesCampaign only integrations.
    customer: CustomerSchema.partial().passthrough().nullable().optional(),
    // Additional fields from the database that components may need
    questionnaire: z.any().nullable().optional(),
    campaignIncentives: z.array(z.any()).nullable().optional(),
    happinessCheck: z.any().nullable().optional(),
    ecFlowTexts: z.any().nullable().optional(),
    senderAddress: z.string().nullable().optional(),
    emailAddress: z.string().nullable().optional(),
    privacyLink: z.string().nullable().optional(),
    customerId: z.string().nullable().optional(),
    campaignGroupId: z.string().nullable().optional(),
    languages: z.any().nullable().optional(),
    scheduleDateTime: z.string().nullable().optional(),
    reuseSameIncentives: z.boolean().nullable().optional(),
    files: z.array(z.any()).nullable().optional(),
    campaignGroup: z.any().nullable().optional(),
    createdAt: z.string().optional(),
    optionalSettings: z.any().nullable().optional(),
  })
  .passthrough() // Allow additional fields without strict validation

const CampaignListSchema = z.array(CampaignSchema)

export { CampaignSchema }

const prefix = '/v2'

// Define query parameters for filtering and sorting campaigns
const CampaignQueryParamsSchema = z.object({
  searchTerm: z.string().optional(),
  customerId: z.string().optional(),
  sortBy: z
    .enum(['name', 'updatedAt', 'startDate', 'endDate', 'typeOfCampaign'])
    .optional()
    .default('updatedAt'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  page: z
    .union([z.string(), z.number().transform((n) => n.toString())])
    .optional()
    .default('1'),
  itemsPerPage: z
    .union([z.string(), z.number().transform((n) => n.toString())])
    .optional()
    .default('10'),
})

// Define pagination metadata schema
const PaginationMetaSchema = z.object({
  totalItems: z.number(),
  itemsPerPage: z.number(),
  currentPage: z.number(),
  totalPages: z.number(),
})

// Define paginated response schema
const PaginatedCampaignResponseSchema = z.object({
  data: CampaignListSchema,
  meta: PaginationMetaSchema,
})

// Create Campaign Input Schema
const CreateCampaignInputSchema = z.object({
  name: z.string(),
  companyName: z.string(),
  startDate: z.string(), // ISO date string
  endDate: z.string(), // ISO date string
  details: z.string(),
  referralCampaign: z.boolean(),
  senderAddress: z.string().optional().nullable(),
  typeOfCampaign: z.string(),
  privacyLink: z.string(),
  campaignCategory: z.string(),
  emailAddress: z.string().optional().nullable(),
  customerId: z.string().optional().nullable(),
  languages: z.any().optional().nullable(), // JSONObject
  scheduleDateTime: z.string().optional().nullable(), // ISO date string
  campaignGroupId: z.string().optional().nullable(),
})

// Update Campaign Details Input Schema
const UpdateCampaignDetailsInputSchema = z.object({
  id: z.string(),
  name: z.string(),
  companyName: z.string(),
  startDate: z.string(), // ISO date string
  endDate: z.string(), // ISO date string
  details: z.string(),
  referralCampaign: z.boolean(),
  senderAddress: z.string().optional().nullable(),
  typeOfCampaign: z.string(),
  campaignCategory: z.string(),
  emailAddress: z.string().optional().nullable(),
  privacyLink: z.string(),
  customerId: z.string().optional().nullable(),
  optionalSettings: z.any().optional().nullable(), // JSONObject
  ecFlowTexts: z.any().optional().nullable(), // JSONObject
  languages: z.any().optional().nullable(), // JSONObject
  scheduleDateTime: z.string().optional().nullable(), // ISO date string
  campaignGroupId: z.string().optional().nullable(),
})

// Save Questionnaire Input Schema
const SaveQuestionnaireInputSchema = z.object({
  id: z.string(),
  questionnaire: z.any().optional().nullable(), // JSONObject
  referral: z.boolean().optional().nullable(),
})

// Attach Campaign Incentives Input Schema
const AttachedIncentiveItemSchema = z.object({
  id: z.string(),
  skuCode: z.string().optional().nullable(),
})

const AttachCampaignIncentivesInputSchema = z.object({
  campaignId: z.string(),
  incentiveItems: z.array(AttachedIncentiveItemSchema),
  referral: z.boolean(),
  reuseSameIncentives: z.boolean(),
})

// Create Offline Campaign Input Schema
const CreateOfflineInputSchema = z.object({
  id: z.string(),
  logo: z.string(),
  salutation: z.any().optional().nullable(), // JSONObject
  postcardTemplate: z.any().optional().nullable(), // JSONObject
})

// Set Happiness Check Form Input Schema
const SetHappinessCheckFormInputSchema = z.object({
  id: z.string(),
  happinessCheck: z.any().optional().nullable(), // JSONObject
})

// Create Online Campaign Input Schema
const CreateOnlineInputSchema = z.object({
  id: z.string(),
  logo: z.string(),
  salutation: z.any().optional().nullable(), // JSONObject
  emailTemplate: z.any().optional().nullable(), // JSONObject
})

// Create Flyer Campaign Input Schema
const CreateFlyerInputSchema = z.object({
  id: z.string(),
  logo: z.string(),
  postcardTemplate: z.any().optional().nullable(), // JSONObject
})

// Create EC Flow Texts Input Schema
const CreateECFlowTextsInputSchema = z.object({
  campaignId: z.string(),
  ecFlowTexts: z.any().optional().nullable(), // JSONObject
  reminders: z.any().optional().nullable(), // JSONObject
  // Emails and Finalise share this call; only Finalise marks the Finalise step reached (Alpha-912).
  step: z.literal('emails').optional(),
})

// Type exports for schemas
export type Campaign = z.infer<typeof CampaignSchema>
export type Customer = z.infer<typeof CustomerSchema>
export type EndUser = z.infer<typeof EndUserSchema>
export type CreateCampaignInput = z.infer<typeof CreateCampaignInputSchema>
export type UpdateCampaignDetailsInput = z.infer<
  typeof UpdateCampaignDetailsInputSchema
>
export type AttachCampaignIncentivesInput = z.infer<
  typeof AttachCampaignIncentivesInputSchema
>
export type CreateOfflineInput = z.infer<typeof CreateOfflineInputSchema>
export type SetHappinessCheckFormInput = z.infer<
  typeof SetHappinessCheckFormInputSchema
>
export type CreateOnlineInput = z.infer<typeof CreateOnlineInputSchema>
export type CreateFlyerInput = z.infer<typeof CreateFlyerInputSchema>
export type CreateECFlowTextsInput = z.infer<
  typeof CreateECFlowTextsInputSchema
>

// End User Schema
const EndUserSchema = z
  .object({
    id: z.string(),
    campaignId: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
    firstName: z.string().optional().nullable(),
    middle: z.string().optional().nullable(),
    lastName: z.string().optional().nullable(),
    company: z.string().optional().nullable(),
    address: z.any().optional().nullable(),
    birthday: z.string().optional().nullable(),
    campaignIncentiveId: z.string().optional().nullable(),
    questionnaireAnswers: z.any().optional().nullable(),
    referralOf: z.string().nullable(),
    happinessAnswers: z.any().optional().nullable(),
    phoneNumber: z.string().optional().nullable(),
    sku: z.string().optional().nullable(),
    digitalCode: z.string().optional().nullable(),
    postnlCode: z.string().optional().nullable(),
    referenceCode: z.number().optional().nullable(),
    uniqueCode: z.string().optional().nullable(),
    isTest: z.boolean().optional().nullable(),
  })
  .passthrough()

// Add Single End User Input Schema
const AddSingleEndUserInputSchema = z.object({
  campaignId: z.string(),
  firstName: z.string(),
  lastName: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  sku: z.string().optional().nullable(),
})

// Update Test End User Input Schema
const UpdateTestEndUserInputSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  sku: z.string().optional().nullable(),
})

export const campaign = c.router({
  createCampaign: {
    method: 'POST',
    path: `${prefix}/campaigns`,
    body: CreateCampaignInputSchema,
    responses: {
      200: z.object({ id: z.string() }),
      400: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Create a new campaign',
  },
  updateCampaignDetails: {
    method: 'PUT',
    path: `${prefix}/campaigns/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: UpdateCampaignDetailsInputSchema,
    responses: {
      200: CampaignSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Update campaign details',
  },
  saveQuestionnaire: {
    method: 'PUT',
    path: `${prefix}/campaigns/:id/questionnaire`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: SaveQuestionnaireInputSchema,
    responses: {
      200: CampaignSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Save campaign questionnaire',
  },
  attachCampaignIncentives: {
    method: 'PUT',
    path: `${prefix}/campaigns/:id/incentives`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: AttachCampaignIncentivesInputSchema,
    responses: {
      200: CampaignSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Attach incentives to campaign',
  },
  createOffline: {
    method: 'POST',
    path: `${prefix}/campaigns/:id/offline`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: CreateOfflineInputSchema,
    responses: {
      200: CampaignSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Create offline campaign creative',
  },
  setHappinessCheckForm: {
    method: 'PUT',
    path: `${prefix}/campaigns/:id/happiness-check`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: SetHappinessCheckFormInputSchema,
    responses: {
      200: z.object({ id: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Set happiness check form for campaign',
  },
  createOnline: {
    method: 'POST',
    path: `${prefix}/campaigns/:id/online`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: CreateOnlineInputSchema,
    responses: {
      200: CampaignSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Create online campaign creative',
  },
  createFlyer: {
    method: 'POST',
    path: `${prefix}/campaigns/:id/flyer`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: CreateFlyerInputSchema,
    responses: {
      200: z.object({ id: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Create flyer campaign creative',
  },
  createECFlowTexts: {
    method: 'POST',
    path: `${prefix}/campaigns/:id/ec-flow-texts`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: CreateECFlowTextsInputSchema,
    responses: {
      200: z.object({ id: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Create EC flow texts for campaign',
  },
  emailTemplate: {
    method: 'GET',
    path: `${prefix}/email-template`,
    query: z.object({
      id: z.string(),
    }),
    responses: {
      200: z.object({ content: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get email template content',
  },
  getAll: {
    method: 'GET',
    path: `${prefix}/campaigns`,
    query: CampaignQueryParamsSchema,
    responses: {
      200: PaginatedCampaignResponseSchema,
    },
    summary: 'Get all campaigns with filtering, sorting and pagination',
  },
  preview: {
    method: 'GET',
    path: `${prefix}/preview`,
    query: z.object({
      fileName: z.string(),
    }),
    responses: {
      200: z.string(),
      404: z.null(),
    },
    summary: 'Get a preview image as base64',
  },
  downloadData: {
    method: 'GET',
    path: `${prefix}/data/:campaignId`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    responses: {
      200: z.any(),
      404: z.object({ message: z.string() }),
    },
    summary: 'Download campaign data as CSV',
  },
  getById: {
    method: 'GET',
    path: `${prefix}/campaigns/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: CampaignSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get a campaign by ID',
  },
  duplicate: {
    method: 'POST',
    path: `${prefix}/campaigns/:id/duplicate`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({}), // Empty object as we don't need any body data
    responses: {
      200: z.string(), // Returns the ID of the duplicated campaign
      404: z.object({ message: z.string() }),
    },
    summary: 'Duplicate a campaign',
  },

  executeCampaign: {
    method: 'POST',
    path: `${prefix}/campaigns/:id/execute`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({}), // Empty object as we don't need any body data
    responses: {
      200: CampaignSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Execute a campaign',
  },
  deleteCampaign: {
    method: 'DELETE',
    path: `${prefix}/campaigns/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: z.object({ success: z.boolean() }),
      404: z.object({ message: z.string() }),
    },
    summary: 'Delete a campaign',
  },
  customers: {
    method: 'GET',
    path: `${prefix}/campaigns/customers`,
    responses: {
      200: z.array(CustomerSchema),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get all customers',
  },
  availableFonts: {
    method: 'GET',
    path: `${prefix}/available-fonts`,
    query: z.object({
      brand: z.string().optional(),
    }),
    responses: {
      200: z.array(z.string()),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get available fonts by brand',
  },
  presignedUrl: {
    method: 'GET',
    path: `${prefix}/presigned-url`,
    query: z.object({
      filePath: z.string(),
    }),
    responses: {
      200: z.object({ url: z.string() }),
      403: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get presigned URL for file download',
  },
  latestPurchaseDateRange: {
    method: 'GET',
    path: `${prefix}/campaigns/:id/latest-purchase-date-range`,
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: z.object({
        startDate: z.string(),
        endDate: z.string(),
      }),
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get latest purchase date range for campaign customer',
  },
  sendReport: {
    method: 'GET',
    path: `${prefix}/campaigns/:campaignId/send-report`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    query: z.object({
      // How many not-sent rows to return; the total is always exact.
      limit: z.coerce.number().min(1).max(5000).optional(),
    }),
    responses: {
      200: z.object({
        campaign: z.object({
          id: z.string(),
          name: z.string(),
          typeOfCampaign: z.string().nullable(),
        }),
        // null until the campaign has been submitted at least once
        report: z
          .object({
            customerId: z.string().nullable(),
            campaignName: z.string().nullable(),
            uploadedCount: z.number(),
            eligibleCount: z.number(),
            acceptedCount: z.number(),
            rejectedCount: z.number(),
            skippedCount: z.number(),
            newsletterId: z.string().nullable(),
            mailcampTotalRecipients: z.number().nullable(),
            mailcampSendSize: z.number().nullable(),
            submittedAt: z.union([z.string(), z.date()]),
            reconciledAt: z.union([z.string(), z.date()]).nullable(),
          })
          .passthrough()
          .nullable(),
        // Live EmailSendLog counts per status: accepted / rejected / skipped
        counts: z.record(z.number()),
        notSent: z.array(
          z.object({
            endUserCampaignId: z.string().nullable(),
            recipientEmail: z.string().nullable(),
            status: z.string(),
            reason: z.string().nullable(),
            at: z.string(),
          }),
        ),
        notSentTotal: z.number(),
        notSentTruncated: z.boolean(),
      }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get the email send report for a campaign',
  },
  endUsers: {
    method: 'GET',
    path: `${prefix}/campaigns/:campaignId/end-users`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    responses: {
      200: z.object({
        endUsers: z.array(EndUserSchema),
        total: z.number(),
      }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get end users for a campaign',
  },
  addSingleEndUser: {
    method: 'POST',
    path: `${prefix}/campaigns/:campaignId/end-users`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    body: AddSingleEndUserInputSchema,
    responses: {
      200: z.object({ id: z.string() }),
      401: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Add a single test end user to campaign',
  },
  updateTestEndUser: {
    method: 'PUT',
    path: `${prefix}/end-users/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: UpdateTestEndUserInputSchema,
    responses: {
      200: EndUserSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Update a test end user',
  },
  deleteTestEndUser: {
    method: 'DELETE',
    path: `${prefix}/end-users/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: z.object({ id: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Delete a test end user',
  },
  clearTestEndUserAnswers: {
    method: 'POST',
    path: `${prefix}/end-users/:id/clear-answers`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({}),
    responses: {
      200: EndUserSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Clear test end user answers',
  },
  sendTestEmail: {
    method: 'POST',
    path: `${prefix}/end-users/:id/send-test-email`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Send test email to end user',
  },
})
